import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useWebSocket } from "../../contexts/WebSocketContext.jsx";
import { apiFetch, apiJson, errorFromResponse } from "../../utils/api.js";
import { userTitle } from "../../utils/users.js";
import { useMe } from "../../hooks/useMe.js";
import { useIsDesktop } from "../../hooks/useIsDesktop.js";
import { useLongPressContextMenu } from "../../hooks/useLongPressContextMenu.js";
import { MsgTextWithLinks } from "../../utils/chatMessageText.jsx";
import { ClientCardModal } from "../components/ClientCardModal.jsx";
import { BottomSheetModal } from "../components/BottomSheetModal.jsx";
import { PageReveal } from "../components/PageReveal.jsx";
import { InlineBanner } from "../components/InlineBanner.jsx";
import { SpellCheckedTextarea } from "../components/SpellCheckedTextarea.jsx";
import { PickClientModal } from "../components/PickClientModal.jsx";
import { MessengerBadge } from "../components/MessengerBadge.jsx";
import { getCachedChatMediaUrl, loadChatMediaIntoCache } from "../../utils/chatMediaCache.js";
import {
  cycleVoiceSpeed,
  getVoicePlaybackSnapshot,
  prefetchVoice,
  seekVoicePlayback,
  subscribeVoicePlayback,
  toggleVoicePlayback,
} from "../../utils/chatVoicePlayback.js";

const PAGE_SIZE = 100;
const CHAT_POLL_INTERVAL_MS = 4000;

function isClientMessage(m) {
  const d = String(m?.direction ?? "").toLowerCase();
  return d === "client_to_manager" || d.endsWith(".client_to_manager");
}

function isManagerMessage(m) {
  const d = String(m?.direction ?? "").toLowerCase();
  return d === "manager_to_client" || d.endsWith(".manager_to_client");
}

function parseMsgTime(iso) {
  const t = Date.parse(iso || "");
  return Number.isFinite(t) ? t : 0;
}

function compareMessagesAsc(a, b) {
  const ta = parseMsgTime(a?.created_at);
  const tb = parseMsgTime(b?.created_at);
  if (ta !== tb) return ta - tb;
  return (Number(a?.id) || 0) - (Number(b?.id) || 0);
}

function trackMessageBounds(msgs, state) {
  for (const m of msgs) {
    const id = Number(m?.id) || 0;
    const t = parseMsgTime(m?.created_at);
    if (id > 0) state.lastId = Math.max(state.lastId, id);
    if (id > 0 && (state.firstId === 0 || id < state.firstId)) state.firstId = id;
    if (m?.created_at && (!state.lastCreatedAt || t >= parseMsgTime(state.lastCreatedAt))) {
      state.lastCreatedAt = m.created_at;
    }
    if (m?.created_at && (!state.firstCreatedAt || t <= parseMsgTime(state.firstCreatedAt))) {
      state.firstCreatedAt = m.created_at;
    }
  }
}
const COMPOSER_TEXTAREA_MAX_HEIGHT_PX = 168;
const VIDEO_NOTE_MAX_SEC = 60;
const CHAT_VIDEO_ACCEPT =
  "video/mp4,video/webm,video/quicktime,video/x-m4v,.mp4,.webm,.mov,.m4v";
const TELEGRAM_CHAT_UPLOAD_MAX_BYTES = 50 * 1024 * 1024;
const MAX_CHAT_UPLOAD_MAX_BYTES = 100 * 1024 * 1024;

function dateKeyInZone(iso, tz) {
  if (!iso || !tz) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat("ru-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const day = parts.find((p) => p.type === "day")?.value;
  return y && m && day ? `${y}-${m}-${day}` : "";
}

function fmtTimeInZone(iso, tz) {
  if (!iso || !tz) return "";
  try {
    return new Date(iso).toLocaleTimeString("ru-RU", {
      timeZone: tz,
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function fmtDateSeparatorLabel(iso, tz) {
  if (!iso || !tz) return "";
  try {
    const d = new Date(iso);
    const key = dateKeyInZone(iso, tz);
    const nowKey = dateKeyInZone(new Date().toISOString(), tz);
    const nowMsk = new Date();
    const nowParts = new Intl.DateTimeFormat("ru-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(nowMsk);
    const y = Number(nowParts.find((p) => p.type === "year")?.value || 0);
    const m = Number(nowParts.find((p) => p.type === "month")?.value || 1);
    const day = Number(nowParts.find((p) => p.type === "day")?.value || 1);
    const yesterdayUtc = new Date(Date.UTC(y, m - 1, day - 1, 0, 0, 0, 0));
    const yKey = dateKeyInZone(yesterdayUtc.toISOString(), tz);

    if (key && key === nowKey) return "Сегодня";
    if (key && key === yKey) return "Вчера";
    return new Intl.DateTimeFormat("ru-RU", {
      timeZone: tz,
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(d);
  } catch {
    return "";
  }
}

function initialsFromName(name) {
  const s = String(name || "").trim();
  if (!s) return "—";
  const parts = s.split(/\s+/).filter(Boolean);
  const a = (parts[0] || "").slice(0, 1);
  const b = (parts[1] || "").slice(0, 1);
  return (a + b).toUpperCase();
}

function isNearBottom(container, thresholdPx = 120) {
  if (!container) return false;
  const scrollTop = container.scrollTop || 0;
  const scrollHeight = container.scrollHeight || 0;
  const clientHeight = container.clientHeight || 0;
  return scrollTop + clientHeight >= scrollHeight - thresholdPx;
}

function isNearTop(container, thresholdPx = 150) {
  if (!container) return false;
  return container.scrollTop < thresholdPx;
}

function scrollToBottom(container) {
  if (container) container.scrollTop = container.scrollHeight;
}

function scrollToBottomSoon(container) {
  if (!container) return;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight;
    });
  });
}

function findMessageElement(root, messageId) {
  if (!root || !messageId) return null;
  const id = Number(messageId);
  if (!id) return null;
  const direct = root.querySelector(`[data-message-id="${id}"]`);
  if (direct) return direct;
  return (
    Array.from(root.querySelectorAll("[data-message-ids]")).find((node) => {
      const ids = (node.getAttribute("data-message-ids") || "")
        .split(",")
        .map((x) => Number(x.trim()))
        .filter((n) => Number.isFinite(n) && n > 0);
      return ids.includes(id);
    }) || null
  );
}

function highlightMessageElement(el) {
  if (!el) return;
  el.classList.add("msgScrollTarget");
  el.scrollIntoView({ block: "center", behavior: "smooth" });
  window.setTimeout(() => el.classList.remove("msgScrollTarget"), 2200);
}

function pickFilenameFromUrl(url) {
  try {
    const u = new URL(url, window.location.origin);
    const last = (u.pathname.split("/").pop() || "").split("?")[0];
    return last || "";
  } catch {
    return "";
  }
}

function extFromMime(mime) {
  const m = String(mime || "").toLowerCase();
  if (!m) return "";
  if (m === "image/jpeg") return "jpg";
  if (m === "image/png") return "png";
  if (m === "image/webp") return "webp";
  if (m === "image/gif") return "gif";
  if (m === "video/mp4") return "mp4";
  if (m === "video/webm") return "webm";
  if (m === "video/quicktime") return "mov";
  const slash = m.indexOf("/");
  return slash >= 0 ? m.slice(slash + 1).split(";")[0] : "";
}

/** Размеры кадра так, как их видит браузер в превью (уже с учётом rotate). */
function probeVideoBlobDimensions(blob) {
  return new Promise((resolve) => {
    if (!blob) {
      resolve({ w: 0, h: 0 });
      return;
    }
    const url = URL.createObjectURL(blob);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.playsInline = true;
    v.muted = true;
    const done = (w, h) => {
      URL.revokeObjectURL(url);
      resolve({ w: w || 0, h: h || 0 });
    };
    v.onloadedmetadata = () => done(v.videoWidth, v.videoHeight);
    v.onerror = () => done(0, 0);
    v.src = url;
  });
}

function isAppleMobileUa() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  return navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1;
}

function devicePlatformHint() {
  if (typeof navigator === "undefined") return "";
  if (isAppleMobileUa()) return "ios";
  if (/android/i.test(navigator.userAgent || "")) return "android";
  return "";
}

async function downloadMediaFromUrl(url, suggestedName) {
  if (!url) return;
  const openInNewTab = () => {
    try {
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      // ignore
    }
  };
  try {
    const res = await fetch(url, { credentials: "omit" });
    if (!res.ok) throw new Error(String(res.status));
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    let name = (suggestedName || pickFilenameFromUrl(url) || "").trim();
    if (!name) {
      const ts = new Date().toISOString().replace(/[:.]/g, "-");
      const ext = extFromMime(blob.type) || "bin";
      name = `media-${ts}.${ext}`;
    } else if (!/\.[a-z0-9]{2,5}$/i.test(name)) {
      const ext = extFromMime(blob.type);
      if (ext) name = `${name}.${ext}`;
    }
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = name;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
  } catch {
    openInNewTab();
  }
}

export function ChatPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { me, loading: meLoading } = useMe();
  const isDesktop = useIsDesktop();
  const displayTz = me?.iana_timezone || "Europe/Moscow";
  const myTelegramId = me?.telegram_id ? Number(me.telegram_id) : null;

  const returnTo = useMemo(() => (searchParams.get("return_to") || "/chat").trim() || "/chat", [searchParams]);
  const cardOpen = useMemo(() => searchParams.get("modal") === "client", [searchParams]);
  const setCardOpen = useCallback(
    (open) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (open) p.set("modal", "client");
          else p.delete("modal");
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const uid = useMemo(() => {
    const n = Number(userId);
    return Number.isFinite(n) ? n : null;
  }, [userId]);

  const { subscribe } = useWebSocket();
  const chatRef = useRef(null);
  const messageInputRef = useRef(null);

  const [title, setTitle] = useState("…");
  const [clientUser, setClientUser] = useState(null);
  const [clientPhone, setClientPhone] = useState("");
  const isMaxMessenger = useMemo(
    () => String(clientUser?.messenger || "").toLowerCase() === "max",
    [clientUser?.messenger],
  );
  const [calling, setCalling] = useState(false);
  /** Встроенные уведомления вместо window.alert (звонок, отправка, медиа и т.д.). */
  const [chatBanner, setChatBanner] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatLoadError, setChatLoadError] = useState(null);
  const [imagePreviewMessageId, setImagePreviewMessageId] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
  const videoLightboxVideoRef = useRef(null);
  const [ctxMenu, setCtxMenu] = useState(null); // { x, y, msg }
  const [ctxMenuPos, setCtxMenuPos] = useState(null); // { x, y }
  const ctxMenuRef = useRef(null);
  const [editingMsg, setEditingMsg] = useState(null); // { id, text }
  const [clientUnreachableOpen, setClientUnreachableOpen] = useState(false);

  const chatPhotos = useMemo(() => collectChatPhotos(messages), [messages]);
  const imagePreviewIndex = useMemo(() => {
    if (imagePreviewMessageId == null) return null;
    const idx = chatPhotos.findIndex((p) => Number(p.messageId) === Number(imagePreviewMessageId));
    return idx >= 0 ? idx : null;
  }, [chatPhotos, imagePreviewMessageId]);

  const openImagePreview = useCallback(
    (payload) => {
      const messageId = Number(payload?.messageId);
      if (!messageId) return;
      if (chatPhotos.some((p) => Number(p.messageId) === messageId)) {
        setImagePreviewMessageId(messageId);
      }
    },
    [chatPhotos]
  );

  const closeImagePreview = useCallback(() => setImagePreviewMessageId(null), []);

  const stepImagePreview = useCallback(
    (delta) => {
      setImagePreviewMessageId((mid) => {
        const idx = chatPhotos.findIndex((p) => Number(p.messageId) === Number(mid));
        if (idx < 0) return mid;
        const next = idx + delta;
        if (next < 0 || next >= chatPhotos.length) return mid;
        return chatPhotos[next].messageId;
      });
    },
    [chatPhotos]
  );

  useEffect(() => {
    if (imagePreviewMessageId != null && imagePreviewIndex == null) {
      setImagePreviewMessageId(null);
    }
  }, [imagePreviewMessageId, imagePreviewIndex]);

  const closeCtxMenu = useCallback(() => setCtxMenu(null), []);
  const cancelEditing = useCallback(() => {
    setEditingMsg(null);
    setText("");
  }, []);


  const stateRef = useRef({
    firstId: 0,
    lastId: 0,
    firstCreatedAt: null,
    lastCreatedAt: null,
    inFlight: false,
    loadingOlder: false,
    hasMoreOlder: true,
    initialScrollPending: false,
  });

  async function loadMessages({ afterId = null, afterCreatedAt = null, beforeId = null, beforeCreatedAt = null, limit = PAGE_SIZE } = {}) {
    const qs = new URLSearchParams({
      user_id: String(uid),
      limit: String(limit),
    });
    if (afterCreatedAt) qs.set("after_created_at", String(afterCreatedAt));
    else if (afterId && Number(afterId) > 0) qs.set("after_id", String(afterId));
    if (beforeCreatedAt) qs.set("before_created_at", String(beforeCreatedAt));
    else if (beforeId && Number(beforeId) > 0) qs.set("before_id", String(beforeId));
    return await apiJson(`/api/messages?${qs.toString()}`);
  }

  async function initialLoad() {
    const root = chatRef.current;
    setLoading(true);
    setChatLoadError(null);
    stateRef.current.firstId = 0;
    stateRef.current.lastId = 0;
    stateRef.current.firstCreatedAt = null;
    stateRef.current.lastCreatedAt = null;
    stateRef.current.hasMoreOlder = true;
    stateRef.current.initialScrollPending = true;
    try {
      const data = await loadMessages({ limit: PAGE_SIZE });
      const msgs = (data.messages || []).slice().sort(compareMessagesAsc);
      setMessages(msgs);
      const meta = data.meta || {};
      if (Number(meta.client_older_hidden) > 0) {
        setChatBanner({
          message: `Выше в истории ещё ${meta.client_older_hidden} входящих от клиента — прокрутите чат вверх`,
          variant: "info",
        });
      } else if (Number(meta.client_total) > 0 && Number(meta.returned) > 0) {
        const returnedClient = msgs.filter(isClientMessage).length;
        if (returnedClient === 0) {
          setChatBanner({
            message: `В базе ${meta.client_total} входящих от клиента, но в текущем окне их нет — прокрутите вверх`,
            variant: "warning",
          });
        }
      }
      const u = data.user;
      if (u) {
        setTitle(userTitle(u));
        setClientUser(u);
        setClientPhone(String(u.phone || "").trim());
      } else {
        setClientUser(null);
        setClientPhone("");
      }
      trackMessageBounds(msgs, stateRef.current);
      stateRef.current.hasMoreOlder = msgs.length >= PAGE_SIZE;
    } catch (err) {
      const msg = err?.message || String(err);
      const is404 = msg.includes("404") || msg.includes("not found") || msg.includes("User not found");
      setChatLoadError(
        is404
          ? "Этот пользователь не найден в боте. Он ещё не написал боту или не привязан к вам."
          : msg || "Не удалось загрузить чат."
      );
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }

  async function loadOlder() {
    const root = chatRef.current;
    if (!root) return;
    if (stateRef.current.loadingOlder || !stateRef.current.hasMoreOlder) return;
    if (!stateRef.current.firstCreatedAt && stateRef.current.firstId <= 1) return;
    stateRef.current.loadingOlder = true;
    try {
      const prevScrollHeight = root.scrollHeight;
      const prevScrollTop = root.scrollTop;
      const data = await loadMessages({
        beforeCreatedAt: stateRef.current.firstCreatedAt,
        limit: PAGE_SIZE,
      });
      const msgs = (data.messages || []).slice().sort(compareMessagesAsc);
      if (!msgs.length) {
        stateRef.current.hasMoreOlder = false;
        return;
      }
      setMessages((prev) => [...msgs, ...prev].sort(compareMessagesAsc));
      trackMessageBounds(msgs, stateRef.current);
      stateRef.current.hasMoreOlder = msgs.length >= PAGE_SIZE;
      setTimeout(() => {
        root.scrollTop = prevScrollTop + (root.scrollHeight - prevScrollHeight);
      }, 0);
    } catch {
      // ignore
    } finally {
      stateRef.current.loadingOlder = false;
    }
  }

  const scrollToMessage = useCallback(
    async (messageId) => {
      const id = Number(messageId);
      if (!id) return;
      const root = chatRef.current;
      if (!root) return;

      let el = findMessageElement(root, id);
      let loads = 0;
      while (!el && stateRef.current.hasMoreOlder && loads < 40) {
        loads += 1;
        await loadOlder();
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
        el = findMessageElement(root, id);
      }

      if (el) {
        highlightMessageElement(el);
        return;
      }
      setChatBanner({
        message: "Исходное сообщение не найдено в истории чата",
        variant: "warning",
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uid]
  );

  async function syncNewMessages() {
    if (uid == null) return;
    if (stateRef.current.inFlight) return;
    stateRef.current.inFlight = true;
    try {
      const root = chatRef.current;
      const shouldScroll = isNearBottom(root);
      const data = await loadMessages({
        afterCreatedAt: stateRef.current.lastCreatedAt,
        afterId: stateRef.current.lastCreatedAt ? null : stateRef.current.lastId,
        limit: PAGE_SIZE,
      });
      const fresh = data?.messages || [];
      if (!fresh.length) return;
      setMessages((prev) => {
        let next = prev;
        for (const msg of fresh) {
          const id = Number(msg?.id) || 0;
          if (!id) continue;
          if (next.some((m) => Number(m.id) === id)) continue;
          next = [...next, msg];
        }
        next = [...next].sort(compareMessagesAsc);
        trackMessageBounds(fresh, stateRef.current);
        return next;
      });
      if (shouldScroll) scrollToBottomSoon(root);
    } catch {
      // ignore sync error, realtime WS can still deliver messages
    } finally {
      stateRef.current.inFlight = false;
    }
  }

  useEffect(() => {
    if (uid == null) return;
    initialLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  useEffect(() => {
    if (loading) return;
    const root = chatRef.current;
    if (!root) return;
    if (stateRef.current.initialScrollPending) {
      stateRef.current.initialScrollPending = false;
      scrollToBottomSoon(root);
    }
  }, [loading, messages.length]);

  useEffect(() => {
    if (uid == null) return;
    const unsub = subscribe((payload) => {
      if (Number(payload?.user_id) !== Number(uid)) return;
      const payloadManagerTg = Number(payload?.message?.manager_telegram_id || payload?.manager_telegram_id || 0);
      const isOutgoing = isManagerMessage(payload?.message);
      if (
        isOutgoing &&
        myTelegramId != null &&
        payloadManagerTg > 0 &&
        payloadManagerTg !== myTelegramId
      ) {
        return;
      }
      if (payload.type === "new_message") {
        const root = chatRef.current;
        const shouldScroll = isNearBottom(root);
        const msg = payload.message;
        if (!msg) return;
        setMessages((prev) => {
          if (prev.some((m) => Number(m.id) === Number(msg.id))) return prev;
          const next = [...prev, msg].sort(compareMessagesAsc);
          trackMessageBounds([msg], stateRef.current);
          return next;
        });
        if (shouldScroll) scrollToBottomSoon(root);
        return;
      }
      if (payload.type === "delete_message") {
        const mid = Number(payload.message_id);
        if (!mid) return;
        setMessages((prev) => prev.filter((m) => Number(m.id) !== mid));
        return;
      }
      if (payload.type === "edit_message") {
        const msg = payload.message;
        const mid = Number(msg?.id);
        if (!mid) return;
        setMessages((prev) => prev.map((m) => (Number(m.id) === mid ? { ...m, ...msg } : m)));
      }
    });
    return unsub;
  }, [uid, subscribe, myTelegramId]);

  useEffect(() => {
    if (uid == null) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      syncNewMessages();
    }, CHAT_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [uid]);

  const normalizeDialPhone = useCallback((raw) => {
    const src = String(raw || "").trim();
    if (!src) return "";
    const hasPlus = src.startsWith("+");
    const digits = src.replace(/\D/g, "");
    if (!digits) return "";
    return hasPlus ? `+${digits}` : digits;
  }, []);

  const callClient = useCallback(async () => {
    const dstNum = normalizeDialPhone(clientPhone);
    if (!dstNum) {
      setChatBanner({
        message: "У клиента не указан телефон для звонка. Укажите номер в карточке клиента.",
        variant: "warning",
      });
      return;
    }
    setChatBanner(null);
    setCalling(true);
    try {
      await apiJson("/api/calls/callback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dst_num: dstNum }),
      });
      setChatBanner({
        message: "Звонок отправлен в АТС. Примите входящий вызов на софтфоне или телефоне.",
        variant: "success",
      });
    } catch (err) {
      setChatBanner({
        message: err?.message || "Не удалось инициировать звонок",
        variant: "error",
      });
    } finally {
      setCalling(false);
    }
  }, [clientPhone, normalizeDialPhone]);

  useEffect(() => {
    if (!chatBanner) return;
    const t = window.setTimeout(() => setChatBanner(null), 8000);
    return () => window.clearTimeout(t);
  }, [chatBanner]);

  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);

  /** Активная сделка с клиентом (для «Этап» / «Отказ» в шапке чата). */
  const [dealQuick, setDealQuick] = useState(null);
  const [dealQuickLoading, setDealQuickLoading] = useState(false);
  const [refusalOpen, setRefusalOpen] = useState(false);
  const [refusalReasons, setRefusalReasons] = useState([]);
  const [refusalReasonId, setRefusalReasonId] = useState(null);
  const [refusalBusy, setRefusalBusy] = useState(false);
  const [nextStageBusy, setNextStageBusy] = useState(false);
  const [chatHeaderMoreOpen, setChatHeaderMoreOpen] = useState(false);
  const chatHeaderMoreRef = useRef(null);
  const [taskCreateOpen, setTaskCreateOpen] = useState(false);
  const [taskCreateTitle, setTaskCreateTitle] = useState("");
  const [taskCreateDescription, setTaskCreateDescription] = useState("");
  const [taskCreateDueAt, setTaskCreateDueAt] = useState("");
  const [taskCreateBusy, setTaskCreateBusy] = useState(false);
  const [composerMenuOpen, setComposerMenuOpen] = useState(false);
  const composerMenuRef = useRef(null);
  const [forwardPickOpen, setForwardPickOpen] = useState(false);
  const [forwardMessageId, setForwardMessageId] = useState(null);

  const chatReturnPath = useMemo(
    () => `${location.pathname || "/chat"}${location.search || ""}`,
    [location.pathname, location.search],
  );

  const loadDealQuick = useCallback(async () => {
    if (uid == null) return;
    setDealQuickLoading(true);
    try {
      const d = await apiJson(
        `/api/deals/active-by-client-user?user_id=${encodeURIComponent(String(uid))}`
      );
      setDealQuick(d?.dealId != null ? d : null);
    } catch {
      setDealQuick(null);
    } finally {
      setDealQuickLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    if (uid == null) {
      setDealQuick(null);
      return;
    }
    if (meLoading) return;
    if (me?.is_dealer) {
      setDealQuick(null);
      return;
    }
    loadDealQuick();
  }, [uid, meLoading, me?.is_dealer, loadDealQuick]);

  useEffect(() => {
    if (!refusalOpen) {
      setRefusalReasonId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiJson("/api/rejection-reasons");
        if (!cancelled && res?.items) setRefusalReasons(res.items);
        else if (!cancelled) setRefusalReasons([]);
      } catch {
        if (!cancelled) setRefusalReasons([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refusalOpen]);

  useEffect(() => {
    if (!chatHeaderMoreOpen) return;
    const onDoc = (e) => {
      const el = chatHeaderMoreRef.current;
      if (el && !el.contains(e.target)) setChatHeaderMoreOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setChatHeaderMoreOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc, { passive: true });
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [chatHeaderMoreOpen]);

  useEffect(() => {
    if (!composerMenuOpen) return;
    const onDoc = (e) => {
      const el = composerMenuRef.current;
      if (el && !el.contains(e.target)) setComposerMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setComposerMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc, { passive: true });
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [composerMenuOpen]);

  const openDealForStageExtras = useCallback(() => {
    if (!dealQuick?.dealId) return;
    const q = new URLSearchParams();
    q.set("return_to", chatReturnPath);
    navigate(`/deals/${dealQuick.dealId}?${q.toString()}`);
  }, [dealQuick?.dealId, chatReturnPath, navigate]);

  const handleChatNextStage = useCallback(async () => {
    if (!dealQuick?.dealId) return;
    const {
      dealId,
      nextStageId,
      nextStageTitle,
      hasQualification,
      qualificationRequiredStage,
      contractSignedStageId,
    } = dealQuick;
    if (!nextStageId) {
      setChatBanner({ message: "Нет следующего этапа", variant: "warning" });
      return;
    }
    if (nextStageId === qualificationRequiredStage && !hasQualification) {
      openDealForStageExtras();
      return;
    }
    const contractStage = contractSignedStageId || "op_contract_signed";
    if (nextStageId === contractStage) {
      openDealForStageExtras();
      return;
    }
    setNextStageBusy(true);
    try {
      await apiJson(`/api/deals/${dealId}/next-stage`, { method: "POST" });
      await loadDealQuick();
      setChatBanner({
        message: nextStageTitle ? `Этап: «${nextStageTitle}»` : "Этап обновлён",
        variant: "success",
      });
    } catch (e) {
      setChatBanner({
        message: e?.message || "Не удалось перевести на следующий этап",
        variant: "error",
      });
    } finally {
      setNextStageBusy(false);
    }
  }, [dealQuick, loadDealQuick, openDealForStageExtras]);

  const handleChatRefuse = useCallback(async () => {
    if (!dealQuick?.dealId || refusalReasonId == null) return;
    setRefusalBusy(true);
    try {
      await apiJson(`/api/deals/${dealQuick.dealId}/refuse`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason_id: refusalReasonId }),
      });
      setRefusalOpen(false);
      setRefusalReasonId(null);
      await loadDealQuick();
      setChatBanner({ message: "Сделка переведена в отказ", variant: "success" });
    } catch (e) {
      setChatBanner({ message: e?.message || "Не удалось оформить отказ", variant: "error" });
    } finally {
      setRefusalBusy(false);
    }
  }, [dealQuick?.dealId, refusalReasonId, loadDealQuick]);

  const openDealFromChat = useCallback(() => {
    if (!dealQuick?.dealId) return;
    const q = new URLSearchParams();
    q.set("return_to", chatReturnPath);
    navigate(`/deals/${dealQuick.dealId}?${q.toString()}`);
  }, [dealQuick?.dealId, chatReturnPath, navigate]);

  const openTaskCreateFromChat = useCallback(() => {
    if (!dealQuick?.dealId) return;
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(10, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setTaskCreateTitle("");
    setTaskCreateDescription("");
    setTaskCreateDueAt(local);
    setTaskCreateOpen(true);
  }, [dealQuick?.dealId]);

  const handleCreateTaskFromChat = useCallback(
    async (e) => {
      e?.preventDefault?.();
      if (!dealQuick?.dealId) return;
      const title = (taskCreateTitle || "").trim();
      if (!title) return;
      setTaskCreateBusy(true);
      try {
        const body = { title };
        const desc = (taskCreateDescription || "").trim();
        if (desc) body.description = desc;
        if (taskCreateDueAt) {
          const d = new Date(taskCreateDueAt);
          if (!Number.isNaN(d.getTime())) body.due_at = d.toISOString();
        }
        await apiJson(`/api/deals/${dealQuick.dealId}/tasks`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        setTaskCreateOpen(false);
        setTaskCreateTitle("");
        setTaskCreateDescription("");
        setTaskCreateDueAt("");
        setChatBanner({ message: "Задача создана", variant: "success" });
      } catch (err) {
        setChatBanner({
          message: err?.message || "Не удалось создать задачу",
          variant: "error",
        });
      } finally {
        setTaskCreateBusy(false);
      }
    },
    [dealQuick?.dealId, taskCreateTitle, taskCreateDescription, taskCreateDueAt]
  );

  const [recordSeconds, setRecordSeconds] = useState(0);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const recordChunksRef = useRef([]);
  const recordMimeRef = useRef("audio/webm");
  const recordSendOnStopRef = useRef(false);
  const recordStartMsRef = useRef(0);
  const recordTickRef = useRef(null);
  const vnStreamRef = useRef(null);
  const vnRecorderRef = useRef(null);
  const vnChunksRef = useRef([]);
  const vnSendRef = useRef(false);
  const vnMimeRef = useRef("video/webm");
  const vnTickRef = useRef(null);
  const vnMaxTimerRef = useRef(null);
  const vnStartedAtRef = useRef(0);
  const vnVideoRef = useRef(null);
  const vnOrientationRef = useRef("portrait");
  const vnFacingRef = useRef("user");
  const localPendingSeqRef = useRef(0);
  const [vnRecording, setVnRecording] = useState(false);
  const [vnRecordSeconds, setVnRecordSeconds] = useState(0);
  const [vnFacingMode, setVnFacingMode] = useState("user");

  useEffect(() => {
    if (recording || vnRecording) return;
    const el = messageInputRef.current;
    if (!el || el.tagName !== "TEXTAREA") return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, COMPOSER_TEXTAREA_MAX_HEIGHT_PX)}px`;
  }, [text, recording, vnRecording, editingMsg]);

  const sendMedia = useCallback(
    async (files, caption, isVoice = false, mediaTypeExtra = null, extraFields = null) => {
      if (!uid || !files?.length) return;
      const uploadLimit = isMaxMessenger ? MAX_CHAT_UPLOAD_MAX_BYTES : TELEGRAM_CHAT_UPLOAD_MAX_BYTES;
      const uploadLimitMb = Math.floor(uploadLimit / (1024 * 1024));
      const tooBig = files.find((f) => (f?.size ?? 0) > uploadLimit);
      if (tooBig) {
        const name = tooBig.name || "файл";
        const sizeMb = Math.ceil((tooBig.size || 0) / (1024 * 1024));
        setChatBanner({
          message: isMaxMessenger
            ? `«${name}» (${sizeMb} МБ): максимум ${uploadLimitMb} МБ.`
            : `«${name}» (${sizeMb} МБ): для клиентов Telegram максимум ${uploadLimitMb} МБ (лимит ботов).`,
          variant: "error",
        });
        return;
      }
      const isVideoNoteSend =
        !isVoice &&
        String(mediaTypeExtra || "").toLowerCase() === "video_note" &&
        files.length === 1;
      if (isVideoNoteSend && isMaxMessenger) {
        setChatBanner({
          message: "Видеокружки для клиентов MAX не поддерживаются.",
          variant: "warning",
        });
        return;
      }
      let pendingLocalId = null;
      if (isVideoNoteSend) {
        pendingLocalId = Date.now() * 1000 + ((localPendingSeqRef.current = (localPendingSeqRef.current + 1) % 1000) + 1);
        const pendingMsg = {
          id: pendingLocalId,
          direction: "manager_to_client",
          content_type: "video_note",
          text: null,
          caption: (caption || "").trim() || null,
          file_id: null,
          file_name: null,
          media_group_id: null,
          created_at: new Date().toISOString(),
          manager_telegram_id: myTelegramId ?? 0,
          _localPending: true,
        };
        setMessages((prev) => [...prev, pendingMsg]);
        scrollToBottomSoon(chatRef.current);
      }
      setSending(true);
      try {
        const form = new FormData();
        form.append("user_id", String(uid));
        if (caption) form.append("caption", caption);
        if (isVoice) form.append("media_type", "voice");
        else if (mediaTypeExtra) form.append("media_type", String(mediaTypeExtra));
        const platformHint = devicePlatformHint();
        if (platformHint) form.append("device_platform", platformHint);
        if (extraFields && typeof extraFields === "object") {
          for (const [k, v] of Object.entries(extraFields)) {
            if (v != null) form.append(k, String(v));
          }
        }
        for (let i = 0; i < files.length; i++) form.append("files", files[i]);
        const res = await apiFetch("/api/send-media", { method: "POST", body: form });
        if (!res.ok) throw await errorFromResponse(res);
        const data = await res.json();
        const list = data?.messages || [];
        setMessages((prev) => {
          let next = pendingLocalId != null ? prev.filter((m) => Number(m.id) !== Number(pendingLocalId)) : prev;
          const added = [];
          for (const msg of list) {
            if (next.some((m) => Number(m.id) === Number(msg.id))) continue;
            added.push(msg);
            next = [...next, msg];
          }
          if (added.length) {
            trackMessageBounds(added, stateRef.current);
            next = next.sort(compareMessagesAsc);
          }
          return next;
        });
        scrollToBottomSoon(chatRef.current);
      } catch (err) {
        if (pendingLocalId != null) {
          setMessages((prev) => prev.filter((m) => Number(m.id) !== Number(pendingLocalId)));
        }
        if (err?.status === 403) setClientUnreachableOpen(true);
        else
          setChatBanner({
            message: err?.message || "Ошибка отправки медиа",
            variant: "error",
          });
      } finally {
        setSending(false);
      }
    },
    [uid, myTelegramId, isMaxMessenger]
  );

  const deleteOwnMessage = useCallback(
    async (msgId) => {
      if (!uid || !msgId) return;
      try {
        await apiJson(`/api/messages/${encodeURIComponent(String(msgId))}`, { method: "DELETE" });
        setMessages((prev) => prev.filter((m) => Number(m.id) !== Number(msgId)));
        if (editingMsg && Number(editingMsg.id) === Number(msgId)) cancelEditing();
      } catch (err) {
        setChatBanner({
          message: err?.message || "Не удалось удалить сообщение",
          variant: "error",
        });
      }
    },
    [uid, editingMsg, cancelEditing]
  );

  const beginEditOwnMessage = useCallback((m) => {
    if (!m?.id) return;
    const ct = String(m.content_type || "").toLowerCase();
    if (ct !== "text") return;
    setEditingMsg({ id: m.id, text: String(m.text || "") });
    setText(String(m.text || ""));
    requestAnimationFrame(() => {
      messageInputRef.current?.focus?.();
      try {
        messageInputRef.current?.setSelectionRange?.(String(m.text || "").length, String(m.text || "").length);
      } catch {
        // ignore
      }
    });
  }, []);

  const submitEdit = useCallback(async () => {
    if (!uid || !editingMsg?.id) return;
    const t = (text || "").trim();
    if (!t) return;
    setSending(true);
    try {
      await apiJson(`/api/messages/${encodeURIComponent(String(editingMsg.id))}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: t }),
      });
      setMessages((prev) => prev.map((m) => (Number(m.id) === Number(editingMsg.id) ? { ...m, text: t } : m)));
      setEditingMsg(null);
      setText("");
    } catch (err) {
      setChatBanner({
        message: err?.message || "Не удалось отредактировать сообщение",
        variant: "error",
      });
    } finally {
      setSending(false);
    }
  }, [uid, editingMsg, text]);

  const copyMessageText = useCallback(async (m) => {
    const value = String(m?.text || m?.caption || "").trim();
    if (!value) {
      setChatBanner({
        message: "В этом сообщении нет текста для копирования",
        variant: "warning",
      });
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Fallback для окружений без clipboard API.
      const ta = document.createElement("textarea");
      ta.value = value;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      try {
        document.execCommand("copy");
      } finally {
        document.body.removeChild(ta);
      }
    }
  }, []);

  const copyMessagePhoto = useCallback(async (m) => {
    if (String(m?.content_type || "").toLowerCase() !== "photo") {
      setChatBanner({ message: "В этом сообщении нет фото", variant: "warning" });
      return;
    }
    const signedUrl = m?.file_url ? String(m.file_url) : "";
    const fileId = m?.file_id != null ? String(m.file_id) : "";
    if (!signedUrl && !fileId) {
      setChatBanner({ message: "Не удалось получить изображение", variant: "error" });
      return;
    }
    try {
      let blob = null;
      if (fileId && signedUrl) {
        try {
          const cached = getCachedChatMediaUrl(fileId);
          const src = cached || (await loadChatMediaIntoCache(fileId, signedUrl));
          const res = await fetch(src);
          if (res.ok) blob = await res.blob();
        } catch {
          // fallback на signed URL ниже
        }
      }
      if (!blob && signedUrl) {
        const res = await fetch(signedUrl, { credentials: "omit" });
        if (!res.ok) throw new Error(String(res.status));
        blob = await res.blob();
      }
      if (!blob) throw new Error("empty");

      const writeBlob = async (b, mime) => {
        if (!navigator.clipboard?.write || typeof ClipboardItem === "undefined") {
          throw new Error("clipboard");
        }
        await navigator.clipboard.write([new ClipboardItem({ [mime]: b })]);
      };

      const mime = blob.type?.startsWith("image/") ? blob.type : "image/png";
      try {
        await writeBlob(blob, mime);
      } catch {
        const bmp = await createImageBitmap(blob);
        const canvas = document.createElement("canvas");
        canvas.width = bmp.width;
        canvas.height = bmp.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          bmp.close();
          throw new Error("canvas");
        }
        ctx.drawImage(bmp, 0, 0);
        bmp.close();
        const pngBlob = await new Promise((resolve, reject) => {
          canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob"))), "image/png");
        });
        await writeBlob(pngBlob, "image/png");
      }
    } catch {
      setChatBanner({
        message: "Не удалось скопировать фото (браузер не поддерживает или нет доступа к буферу)",
        variant: "error",
      });
    }
  }, []);

  const openMessageMenu = useCallback((m, x, y) => {
    if (!m) return;
    setCtxMenu({ x, y, msg: m });
    setCtxMenuPos({ x, y });
  }, []);

  const beginForwardMessage = useCallback((m) => {
    if (!m?.id) return;
    setForwardMessageId(Number(m.id));
    setForwardPickOpen(true);
  }, []);

  const handleForwardPick = useCallback(
    async (picked) => {
      const mid = forwardMessageId;
      const toTgNum = Number(picked?.telegram_id);
      const toCrmId = Number(picked?.id);
      if (mid == null || uid == null) return;
      if (!Number.isFinite(toTgNum) || toTgNum <= 0) {
        setChatBanner({ message: "У выбранного клиента нет внешнего id для доставки в мессенджер", variant: "error" });
        return;
      }
      const fromTg = Number(clientUser?.telegram_id);
      if (!Number.isFinite(fromTg) || fromTg <= 0) {
        setChatBanner({ message: "Не удалось определить чат отправителя", variant: "error" });
        return;
      }
      if (toTgNum === fromTg || (Number.isFinite(toCrmId) && toCrmId === Number(uid))) {
        setChatBanner({ message: "Нельзя переслать в этот же чат", variant: "warning" });
        return;
      }
      try {
        await apiJson("/api/messages/forward", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message_id: mid,
            to_user_telegram_id: toTgNum,
            from_user_telegram_id: fromTg,
          }),
        });
        // Закрываем модалку синхронно и переходим в чат получателя.
        // Делаем это в макрозадаче, чтобы React успел обработать setState
        // и снять оверлей до того, как сменится маршрут — иначе на части
        // браузеров оверлей оставался поверх и казалось, что переход не произошёл.
        setForwardPickOpen(false);
        setForwardMessageId(null);
        const target = `/chat/${encodeURIComponent(String(toCrmId))}`;
        window.setTimeout(() => {
          try {
            navigate(target);
          } catch {
            // Hard fallback: если SPA-навигация по какой-то причине не сработала
            // (редкий кейс с размонтированным роутером), уходим жёстким переходом.
            const base = String(import.meta.env.BASE_URL || "/").replace(/\/+$/, "");
            window.location.assign(`${base}${target}`);
          }
        }, 0);
      } catch (e) {
        setChatBanner({
          message: e instanceof Error ? e.message : String(e || "Не удалось переслать"),
          variant: "error",
        });
      }
    },
    [forwardMessageId, uid, navigate, clientUser?.telegram_id]
  );

  useEffect(() => {
    if (!ctxMenu) return;
    const M = 8; // margin from screen edges
    const raf = requestAnimationFrame(() => {
      const el = ctxMenuRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vw = window.innerWidth || 0;
      const vh = window.innerHeight || 0;
      let x = Number(ctxMenu.x) || 0;
      let y = Number(ctxMenu.y) || 0;
      // Clamp to viewport so menu never goes off-screen
      if (vw > 0) {
        x = Math.max(M, Math.min(x, vw - rect.width - M));
      } else {
        x = Math.max(M, x);
      }
      if (vh > 0) {
        y = Math.max(M, Math.min(y, vh - rect.height - M));
      } else {
        y = Math.max(M, y);
      }
      setCtxMenuPos({ x, y });
    });
    return () => cancelAnimationFrame(raf);
  }, [ctxMenu]);

  const startRecording = useCallback(() => {
    if (mediaRecorderRef.current || streamRef.current) return;
    if (vnRecorderRef.current || vnStreamRef.current) return;
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        streamRef.current = stream;
        const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : "audio/webm";
        recordChunksRef.current = [];
        recordMimeRef.current = mime;
        recordSendOnStopRef.current = false;
        recordStartMsRef.current = Date.now();
        setRecordSeconds(0);
        const mr = new MediaRecorder(stream, { mimeType: mime });
        mediaRecorderRef.current = mr;
        mr.ondataavailable = (e) => {
          if (e.data.size) recordChunksRef.current.push(e.data);
        };
        mr.onstop = () => {
          stream.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
          mediaRecorderRef.current = null;
          setRecording(false);
          const chunks = recordChunksRef.current || [];
          const sendIt = !!recordSendOnStopRef.current;
          recordChunksRef.current = [];
          recordSendOnStopRef.current = false;
          if (sendIt && chunks.length && uid) {
            const blob = new Blob(chunks, { type: recordMimeRef.current || mime });
            const ext = (recordMimeRef.current || mime).includes("webm") ? "webm" : "ogg";
            const file = new File([blob], `voice.${ext}`, { type: blob.type });
            sendMedia([file], undefined, true);
          }
        };
        mr.start(200);
        setRecording(true);
      })
      .catch((err) => {
        setChatBanner({
          message: "Нет доступа к микрофону: " + (err?.message || String(err)),
          variant: "error",
        });
      });
  }, [uid, sendMedia]);

  const stopRecording = useCallback((opts = { send: false }) => {
    recordSendOnStopRef.current = !!opts?.send;
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const toggleVoice = useCallback(() => {
    // Кнопка микрофона во время записи — отмена (не отправлять)
    if (recording) stopRecording({ send: false });
    else startRecording();
  }, [recording, startRecording, stopRecording]);

  const stopVideoNoteRecording = useCallback((opts = { send: false }) => {
    vnSendRef.current = !!opts?.send;
    if (vnMaxTimerRef.current) {
      window.clearTimeout(vnMaxTimerRef.current);
      vnMaxTimerRef.current = null;
    }
    const mr = vnRecorderRef.current;
    if (mr && mr.state !== "inactive") {
      mr.stop();
    } else {
      const stream = vnStreamRef.current;
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
        vnStreamRef.current = null;
      }
      vnRecorderRef.current = null;
      setVnRecording(false);
      vnChunksRef.current = [];
      vnSendRef.current = false;
    }
  }, []);

  const startVideoNoteRecording = useCallback((facingArg) => {
    if (isMaxMessenger) {
      setChatBanner({
        message: "Видеокружки для клиентов MAX не поддерживаются.",
        variant: "warning",
      });
      return;
    }
    if (vnRecorderRef.current || vnStreamRef.current) return;
    if (recording) return;
    const useFacing = facingArg || vnFacingMode || "user";
    const pickMime = () => {
      const candidates = [
        "video/webm;codecs=vp9,opus",
        "video/webm;codecs=vp8,opus",
        "video/webm;codecs=vp8",
        "video/webm",
      ];
      for (const m of candidates) {
        if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m)) return m;
      }
      return "video/webm";
    };
    navigator.mediaDevices
      .getUserMedia({
        audio: true,
        video: {
          facingMode: { ideal: useFacing },
          width: { ideal: 720, max: 1280 },
          height: { ideal: 1280, max: 1920 },
          aspectRatio: { ideal: 9 / 16 },
        },
      })
      .then((stream) => {
        vnStreamRef.current = stream;
        const actualFacing =
          stream?.getVideoTracks?.()[0]?.getSettings?.()?.facingMode || useFacing || "user";
        vnFacingRef.current = actualFacing;
        setVnFacingMode(actualFacing);
        try {
          const isPortrait = window.matchMedia?.("(orientation: portrait)")?.matches;
          vnOrientationRef.current = isPortrait ? "portrait" : "landscape";
        } catch {
          vnOrientationRef.current = "portrait";
        }
        const mime = pickMime();
        vnChunksRef.current = [];
        vnMimeRef.current = mime;
        vnSendRef.current = false;
        vnStartedAtRef.current = Date.now();
        setVnRecordSeconds(0);
        let mr;
        try {
          mr = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 2_500_000 });
        } catch {
          try {
            mr = new MediaRecorder(stream, { mimeType: mime });
          } catch {
            mr = new MediaRecorder(stream);
          }
        }
        vnRecorderRef.current = mr;
        mr.ondataavailable = (e) => {
          if (e.data.size) vnChunksRef.current.push(e.data);
        };
        mr.onstop = async () => {
          stream.getTracks().forEach((t) => t.stop());
          if (vnStreamRef.current === stream) vnStreamRef.current = null;
          if (vnRecorderRef.current === mr) vnRecorderRef.current = null;
          setVnRecording(false);
          if (vnMaxTimerRef.current) {
            window.clearTimeout(vnMaxTimerRef.current);
            vnMaxTimerRef.current = null;
          }
          const chunks = vnChunksRef.current || [];
          const sendIt = !!vnSendRef.current;
          vnChunksRef.current = [];
          vnSendRef.current = false;
          if (sendIt && chunks.length && uid) {
            const blob = new Blob(chunks, { type: vnMimeRef.current || mime });
            const dims = await probeVideoBlobDimensions(blob);
            const file = new File([blob], "videonote.webm", { type: blob.type || "video/webm" });
            const extra = {
              orientation: vnOrientationRef.current || "portrait",
              facing: vnFacingRef.current || useFacing || "user",
            };
            if (dims.w > 0 && dims.h > 0) {
              extra.record_w = dims.w;
              extra.record_h = dims.h;
            }
            if (isAppleMobileUa()) extra.device_platform = "ios";
            sendMedia([file], undefined, false, "video_note", extra);
          }
        };
        mr.start(250);
        setVnRecording(true);
        vnMaxTimerRef.current = window.setTimeout(() => {
          if (vnRecorderRef.current?.state === "recording") {
            vnSendRef.current = true;
            vnRecorderRef.current.stop();
          }
        }, VIDEO_NOTE_MAX_SEC * 1000);
      })
      .catch((err) => {
        setChatBanner({
          message: "Нет доступа к камере/микрофону: " + (err?.message || String(err)),
          variant: "error",
        });
      });
  }, [uid, recording, sendMedia, vnFacingMode, isMaxMessenger]);

  const toggleVideoNote = useCallback(() => {
    if (isMaxMessenger) {
      setChatBanner({
        message: "Видеокружки для клиентов MAX не поддерживаются.",
        variant: "warning",
      });
      return;
    }
    if (vnRecording) stopVideoNoteRecording({ send: false });
    else startVideoNoteRecording();
  }, [vnRecording, startVideoNoteRecording, stopVideoNoteRecording, isMaxMessenger]);

  // Сменить переднюю / заднюю камеру (как в ТГ).
  // Останавливаем текущую запись без отправки, поднимаем поток с другим facingMode.
  // Технически это рестарт записи (предыдущие чанки сбрасываются) — для веб-MediaRecorder это безопасный путь.
  const switchVideoNoteCamera = useCallback(() => {
    if (isMaxMessenger) return;
    if (!vnRecording) return;
    const next = (vnFacingMode === "user" ? "environment" : "user");
    // Гасим без отправки и стартуем новый поток с другим фейсингом.
    try {
      const mr = vnRecorderRef.current;
      vnSendRef.current = false;
      if (vnMaxTimerRef.current) {
        window.clearTimeout(vnMaxTimerRef.current);
        vnMaxTimerRef.current = null;
      }
      if (mr && mr.state !== "inactive") {
        mr.stop();
      } else {
        const stream = vnStreamRef.current;
        if (stream) {
          stream.getTracks().forEach((t) => t.stop());
          vnStreamRef.current = null;
        }
        vnRecorderRef.current = null;
        setVnRecording(false);
        vnChunksRef.current = [];
      }
    } catch {
      // ignore
    }
    setVnFacingMode(next);
    // Дожидаемся освобождения камеры — у некоторых браузеров камера не отдаётся, пока трек не закроется.
    window.setTimeout(() => startVideoNoteRecording(next), 120);
  }, [vnRecording, vnFacingMode, startVideoNoteRecording, isMaxMessenger]);

  // Привязываем активный поток к <video>-превью кружка.
  useEffect(() => {
    const v = vnVideoRef.current;
    if (!v) return;
    if (vnRecording && vnStreamRef.current) {
      try {
        if (v.srcObject !== vnStreamRef.current) {
          v.srcObject = vnStreamRef.current;
        }
        v.muted = true;
        v.playsInline = true;
        const p = v.play?.();
        if (p && typeof p.catch === "function") p.catch(() => {});
      } catch {
        // ignore
      }
    } else {
      try {
        v.pause?.();
        v.srcObject = null;
      } catch {
        // ignore
      }
    }
  }, [vnRecording, vnFacingMode]);

  useEffect(() => {
    if (!recording) {
      if (recordTickRef.current) {
        clearInterval(recordTickRef.current);
        recordTickRef.current = null;
      }
      setRecordSeconds(0);
      return;
    }
    if (recordTickRef.current) clearInterval(recordTickRef.current);
    recordTickRef.current = setInterval(() => {
      const start = recordStartMsRef.current || Date.now();
      const sec = Math.max(0, Math.floor((Date.now() - start) / 1000));
      setRecordSeconds(sec);
    }, 200);
    return () => {
      if (recordTickRef.current) {
        clearInterval(recordTickRef.current);
        recordTickRef.current = null;
      }
    };
  }, [recording]);

  useEffect(() => {
    if (!vnRecording) {
      if (vnTickRef.current) {
        clearInterval(vnTickRef.current);
        vnTickRef.current = null;
      }
      setVnRecordSeconds(0);
      return;
    }
    if (vnTickRef.current) clearInterval(vnTickRef.current);
    vnTickRef.current = setInterval(() => {
      const start = vnStartedAtRef.current || Date.now();
      const sec = Math.min(VIDEO_NOTE_MAX_SEC, Math.max(0, Math.floor((Date.now() - start) / 1000)));
      setVnRecordSeconds(sec);
      if (sec >= VIDEO_NOTE_MAX_SEC && vnRecorderRef.current?.state === "recording") {
        vnSendRef.current = true;
        vnRecorderRef.current.stop();
      }
    }, 200);
    return () => {
      if (vnTickRef.current) {
        clearInterval(vnTickRef.current);
        vnTickRef.current = null;
      }
    };
  }, [vnRecording]);

  useEffect(() => {
    if (imagePreviewIndex == null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeImagePreview();
      else if (e.key === "ArrowLeft") stepImagePreview(-1);
      else if (e.key === "ArrowRight") stepImagePreview(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [imagePreviewIndex, closeImagePreview, stepImagePreview]);

  const closeVideoPreview = useCallback(() => {
    try {
      const el = videoLightboxVideoRef.current;
      if (el && document.fullscreenElement === el) {
        document.exitFullscreen().catch(() => {});
      }
    } catch {
      // ignore
    }
    setVideoPreviewUrl(null);
  }, []);

  useEffect(() => {
    if (!videoPreviewUrl) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeVideoPreview();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [videoPreviewUrl, closeVideoPreview]);

  return (
    <>
      <header className="topbar topbarChat">
        <div className="topbarChatSide topbarChatSideLeft">
          <button
            type="button"
            className="topbarChatAvatar"
            aria-label="Открыть карточку клиента"
            onClick={() => setCardOpen(true)}
          >
            {initialsFromName(clientUser ? userTitle(clientUser) : title)}
          </button>
        </div>
        <div className="topbarChatTitleWrap">
          <div className="topbarTitleRow">
            <div className="topbarTitleCluster">
              <div className="topbarTitle" id="chatUserName">
                {title}
              </div>
              {clientUser?.messenger ? <MessengerBadge messenger={clientUser.messenger} /> : null}
            </div>
          </div>
        </div>
        <div className="topbarChatSide topbarChatSideRight">
          <div className="topbarChatMoreWrap" ref={chatHeaderMoreRef}>
            <button
              className="topbarCardBtn"
              type="button"
              title="Действия"
              aria-label="Меню действий"
              aria-expanded={chatHeaderMoreOpen}
              aria-haspopup="true"
              onClick={() => setChatHeaderMoreOpen((o) => !o)}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                <circle cx="12" cy="5" r="2" />
                <circle cx="12" cy="12" r="2" />
                <circle cx="12" cy="19" r="2" />
              </svg>
            </button>
            {chatHeaderMoreOpen ? (
              <div className="topbarChatMoreMenu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="topbarChatMoreItem"
                  title="Позвонить клиенту"
                  disabled={calling}
                  onClick={() => {
                    setChatHeaderMoreOpen(false);
                    void callClient();
                  }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.33 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                  Позвонить клиенту
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="topbarChatMoreItem"
                  title="Карточка клиента"
                  onClick={() => {
                    setChatHeaderMoreOpen(false);
                    setCardOpen(true);
                  }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  Карточка клиента
                </button>
                {!me?.is_dealer && dealQuick?.dealId ? (
                  <>
                    <button
                      type="button"
                      role="menuitem"
                      className="topbarChatMoreItem"
                      title="Открыть карточку сделки"
                      onClick={() => {
                        setChatHeaderMoreOpen(false);
                        openDealFromChat();
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M3 7h18" />
                        <path d="M5 7v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7" />
                        <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                      </svg>
                      Открыть сделку
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="topbarChatMoreItem"
                      title="Создать задачу по этой сделке"
                      onClick={() => {
                        setChatHeaderMoreOpen(false);
                        openTaskCreateFromChat();
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M9 11l3 3L22 4" />
                        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                      </svg>
                      Поставить задачу
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="topbarChatMoreItem"
                      title={dealQuick.nextStageTitle ? `Следующий этап: ${dealQuick.nextStageTitle}` : "Следующий этап сделки"}
                      disabled={dealQuickLoading || nextStageBusy || !dealQuick.nextStageId}
                      onClick={() => {
                        setChatHeaderMoreOpen(false);
                        void handleChatNextStage();
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                      {dealQuick.nextStageTitle ? `Этап: ${dealQuick.nextStageTitle}` : "Следующий этап"}
                    </button>
                    {dealQuick.canRefuse ? (
                      <button
                        type="button"
                        role="menuitem"
                        className="topbarChatMoreItem topbarChatMoreItem--danger"
                        title="Перевести сделку в отказ"
                        disabled={dealQuickLoading}
                        onClick={() => {
                          setChatHeaderMoreOpen(false);
                          setRefusalOpen(true);
                        }}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                        </svg>
                        Отказ
                      </button>
                    ) : null}
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </header>

      {chatBanner ? (
        <InlineBanner
          message={chatBanner.message}
          variant={chatBanner.variant}
          onClose={() => setChatBanner(null)}
          className="chatScreenCallHint"
        />
      ) : null}

      <main
        className="chat"
        id="chat"
      >
        <div
          className="screenScroll"
          id="chatScroll"
          ref={chatRef}
          onScroll={() => {
            const root = chatRef.current;
            if (root && isNearTop(root) && stateRef.current.hasMoreOlder) {
              loadOlder();
            }
          }}
        >
        {chatLoadError ? (
          <div className="chatErrorBlock">
            <p className="chatErrorText">{chatLoadError}</p>
            <button
              type="button"
              className="linkBtn chatErrorBackBtn"
              onClick={() => navigate(returnTo)}
            >
              Вернуться
            </button>
          </div>
        ) : (
          <>
            <PageReveal ready={!loading}>
              {(() => {
                const groups = messageGroups(messages);
                const out = [];
                let prevDateKey = "";
                for (let i = 0; i < groups.length; i++) {
                  const group = groups[i];
                  const first = group?.[0];
                  const key = dateKeyInZone(first?.created_at, displayTz);
                  if (key && key !== prevDateKey) {
                    prevDateKey = key;
                    out.push(
                      <div key={`date-${key}-${i}`} className="chatDateSeparator" aria-hidden="true">
                        <span className="chatDatePill">{fmtDateSeparatorLabel(first?.created_at, displayTz)}</span>
                      </div>
                    );
                  }
                  out.push(
                    group.length > 1 ? (
                      <MessageAlbum
                        key={`album-${first?.media_group_id ?? first?.id}-${first?.id}`}
                        group={group}
                        uid={uid}
                        onOpenImage={openImagePreview}
                        onOpenMenu={openMessageMenu}
                        onGoToReply={scrollToMessage}
                        messages={messages}
                        myTelegramId={myTelegramId}
                        displayTz={displayTz}
                      />
                    ) : (
                      <MessageBubble
                        key={String(first?.id)}
                        m={first}
                        uid={uid}
                        onOpenImage={openImagePreview}
                        onOpenVideo={setVideoPreviewUrl}
                        onOpenMenu={openMessageMenu}
                        onGoToReply={scrollToMessage}
                        messages={messages}
                        myTelegramId={myTelegramId}
                        displayTz={displayTz}
                      />
                    )
                  );
                }
                return out;
              })()}
            </PageReveal>
          </>
        )}
        </div>
      </main>

      {ctxMenu ? (
        <div className="msgCtxBackdrop" onClick={closeCtxMenu} onContextMenu={(e) => { e.preventDefault(); closeCtxMenu(); }}>
          <div
            className="msgCtxMenu"
            ref={ctxMenuRef}
            style={{ left: (ctxMenuPos?.x ?? ctxMenu.x), top: (ctxMenuPos?.y ?? ctxMenu.y) }}
            role="menu"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="msgCtxItem"
              title="Скопировать"
              aria-label="Скопировать"
              onClick={async () => {
                const m = ctxMenu.msg;
                closeCtxMenu();
                await copyMessageText(m);
              }}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            </button>
            {String(ctxMenu.msg?.content_type || "").toLowerCase() === "photo" &&
            (ctxMenu.msg?.file_url || ctxMenu.msg?.file_id) ? (
              <button
                type="button"
                className="msgCtxItem"
                title="Скопировать фото"
                aria-label="Скопировать фото"
                onClick={async () => {
                  const m = ctxMenu.msg;
                  closeCtxMenu();
                  await copyMessagePhoto(m);
                }}
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="m21 15-5-5L5 21" />
                </svg>
              </button>
            ) : null}
            {!me?.is_dealer ? (
              <button
                type="button"
                className="msgCtxItem"
                title="Переслать в другой чат"
                aria-label="Переслать в другой чат"
                onClick={() => {
                  const m = ctxMenu.msg;
                  closeCtxMenu();
                  beginForwardMessage(m);
                }}
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M4 12v8a2 2 0 0 0 2 2h8" />
                  <path d="M16 6h6v6" />
                  <path d="M14 14L22 6" />
                </svg>
              </button>
            ) : null}
            {String(ctxMenu.msg?.content_type || "").toLowerCase() === "text" ? (
              <button
                type="button"
                className="msgCtxItem"
                title="Редактировать"
                aria-label="Редактировать"
                onClick={() => {
                  const m = ctxMenu.msg;
                  closeCtxMenu();
                  beginEditOwnMessage(m);
                }}
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                </svg>
              </button>
            ) : null}
            <button
              type="button"
              className="msgCtxItem msgCtxDanger"
              title="Удалить"
              aria-label="Удалить"
              onClick={() => {
                const m = ctxMenu.msg;
                closeCtxMenu();
                deleteOwnMessage(m.id);
              }}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6" />
                <path d="M14 11v6" />
                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
              </svg>
            </button>
          </div>
        </div>
      ) : null}

      {!chatLoadError && vnRecording ? (
        <div className="videoNotePreviewOverlay" role="region" aria-label="Запись видеокружка">
          <div className="videoNotePreviewCircle">
            <video
              ref={vnVideoRef}
              className={`videoNotePreviewVideo${vnFacingMode === "user" ? " videoNotePreviewVideo--mirrored" : ""}`}
              autoPlay
              muted
              playsInline
            />
            <div className="videoNotePreviewBadge">
              <span className="videoNotePreviewDot" />
              <span className="videoNotePreviewTime">
                {formatTime(vnRecordSeconds)} <span className="videoNotePreviewTimeMax">/ {formatTime(VIDEO_NOTE_MAX_SEC)}</span>
              </span>
            </div>
            <button
              type="button"
              className="videoNotePreviewSwitchBtn"
              onClick={switchVideoNoteCamera}
              title="Сменить камеру"
              aria-label="Сменить камеру"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 8a9 9 0 0 0-15.5-3" />
                <polyline points="21 3 21 8 16 8" />
                <path d="M3 16a9 9 0 0 0 15.5 3" />
                <polyline points="3 21 3 16 8 16" />
              </svg>
            </button>
          </div>
          <div className="videoNotePreviewHint">Нажмите ✕ — отменить, ↑ — отправить</div>
        </div>
      ) : null}

      {!chatLoadError && (
      <footer className="footer">
        {editingMsg ? (
          <div className="composerEditBar" role="status" aria-live="polite">
            <span className="composerEditTitle">Редактирование</span>
            <button type="button" className="composerEditCancel" onClick={cancelEditing} disabled={sending}>
              Отмена
            </button>
          </div>
        ) : null}
        <form
          className="composer"
          id="composer"
          onSubmit={async (e) => {
            e.preventDefault();
            const t = (text || "").trim();
            if (uid == null) return;
            if (editingMsg) {
              await submitEdit();
              return;
            }
            if (recording) {
              stopRecording({ send: true });
              return;
            }
            if (vnRecording) {
              stopVideoNoteRecording({ send: true });
              return;
            }
            if (!t) return;
            setSending(true);
            try {
              const res = await apiJson("/api/send", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user_id: uid, use_telegram_id: false, text: t }),
              });
              setText("");
              const msg = res?.message;
              if (msg?.id) {
                setMessages((prev) => {
                  if (prev.some((m) => Number(m.id) === Number(msg.id))) return prev;
                  const next = [...prev, msg].sort(compareMessagesAsc);
                  trackMessageBounds([msg], stateRef.current);
                  return next;
                });
              }
              scrollToBottomSoon(chatRef.current);
            } catch (err) {
              if (err?.status === 403) setClientUnreachableOpen(true);
              else
                setChatBanner({
                  message: `Ошибка отправки: ${String(err?.message || err)}`,
                  variant: "error",
                });
            } finally {
              setSending(false);
            }
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            className="chatFileInput"
            accept="image/*,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            multiple
            onChange={async (e) => {
              const files = e.target.files;
              if (!files?.length) return;
              await sendMedia(Array.from(files), text.trim() || undefined);
              setText("");
              e.target.value = "";
            }}
            aria-label="Прикрепить файл"
          />
          {/* Слева: «+» с поповером действий, либо «×» отмена записи */}
          {recording || vnRecording ? (
            <button
              type="button"
              className="composerActionBtn composerActionBtn--recording"
              onClick={recording ? toggleVoice : toggleVideoNote}
              title="Отменить запись"
              aria-label="Отменить запись"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          ) : (
          <div className="composerPlusWrap" ref={composerMenuRef}>
            <button
              type="button"
              className="composerPlusBtn"
              onClick={() => setComposerMenuOpen((o) => !o)}
              title="Добавить"
              aria-label="Действия"
              aria-expanded={composerMenuOpen}
              aria-haspopup="true"
              disabled={sending || !!editingMsg}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
            {composerMenuOpen ? (
              <div className="composerActionsMenu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="composerActionsItem"
                  onClick={() => {
                    setComposerMenuOpen(false);
                    const inp = fileInputRef.current;
                    if (!inp) return;
                    inp.accept = "image/*";
                    inp.click();
                  }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="9.5" r="1.5" />
                    <path d="M21 15l-5-5L5 21" />
                  </svg>
                  Фото
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="composerActionsItem"
                  onClick={() => {
                    setComposerMenuOpen(false);
                    const inp = fileInputRef.current;
                    if (!inp) return;
                    inp.accept = ".pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
                    inp.click();
                  }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="9" y1="13" x2="15" y2="13" />
                    <line x1="9" y1="17" x2="13" y2="17" />
                  </svg>
                  Документ
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="composerActionsItem"
                  disabled={sending || !!editingMsg}
                  onClick={() => {
                    setComposerMenuOpen(false);
                    const inp = fileInputRef.current;
                    if (!inp) return;
                    inp.accept = CHAT_VIDEO_ACCEPT;
                    inp.click();
                  }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="2" y="5" width="20" height="14" rx="2" />
                    <polygon points="10 9 16 12 10 15 10 9" fill="currentColor" stroke="none" />
                  </svg>
                  Видео
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="composerActionsItem"
                  disabled={sending || !!editingMsg}
                  onClick={() => {
                    setComposerMenuOpen(false);
                    if (!recording) toggleVoice();
                  }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                    <line x1="12" y1="19" x2="12" y2="23" />
                    <line x1="8" y1="23" x2="16" y2="23" />
                  </svg>
                  Голосовое сообщение
                </button>
                {!isDesktop && !isMaxMessenger ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="composerActionsItem"
                    disabled={sending || !!editingMsg || uid == null}
                    onClick={() => {
                      setComposerMenuOpen(false);
                      if (!vnRecording) toggleVideoNote();
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <circle cx="12" cy="12" r="9" />
                      <circle cx="12" cy="12" r="4" fill="currentColor" stroke="none" />
                    </svg>
                    Видеокружок
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
          )}

          {/* Поле ввода или таймер записи на всю ширину */}
          {recording ? (
            <div className="composerRecordTimer" aria-label="Запись голосового сообщения">
              {formatTime(recordSeconds)}
            </div>
          ) : vnRecording ? (
            <div className="composerRecordTimer composerRecordTimerVideoNote" aria-label="Запись видеокружка">
              {formatTime(vnRecordSeconds)} / {formatTime(VIDEO_NOTE_MAX_SEC)}
            </div>
          ) : (
            <SpellCheckedTextarea
              className="input composerMessageInput"
              id="messageInput"
              ref={messageInputRef}
              rows={1}
              placeholder="Введите сообщение…"
              autoComplete="off"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape" && editingMsg) {
                  e.preventDefault();
                  cancelEditing();
                  return;
                }
                // Enter отправляет сообщение, Shift+Enter — перенос строки.
                if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent?.isComposing) {
                  e.preventDefault();
                  if (!sending) {
                    e.currentTarget.form?.requestSubmit();
                  }
                }
              }}
              onPaste={async (e) => {
                if (editingMsg) return;
                const items = e.clipboardData?.items;
                if (!items?.length) return;
                const imageFiles = [];
                for (const item of items) {
                  if (item.kind === "file" && (item.type || "").startsWith("image/")) {
                    const f = item.getAsFile();
                    if (f) imageFiles.push(f);
                  }
                }
                if (!imageFiles.length) return;
                e.preventDefault();
                await sendMedia(imageFiles, text.trim() || undefined);
                setText("");
              }}
            />
          )}

          {/* Правая часть: при записи — «Отправить» (подтверждает запись), при пустом тексте — мик/кружок (как в Telegram), иначе — «Отправить» */}
          {recording || vnRecording ? (
            <button
              className="sendBtn"
              type="submit"
              disabled={sending}
              aria-label="Отправить запись"
              title="Отправить запись"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          ) : null}
          {!recording && vnRecording ? (
            <button
              type="button"
              className="composerActionBtn"
              onClick={switchVideoNoteCamera}
              title="Сменить камеру"
              aria-label="Сменить камеру"
              disabled={sending}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 8a9 9 0 0 0-15.5-3" />
                <polyline points="21 3 21 8 16 8" />
                <path d="M3 16a9 9 0 0 0 15.5 3" />
                <polyline points="3 21 3 16 8 16" />
              </svg>
            </button>
          ) : null}
          {!recording && !vnRecording && !editingMsg && !text.trim() ? (
            <span className="composerActionGroup">
              <button
                type="button"
                className="composerActionBtn"
                onClick={toggleVoice}
                title="Голосовое сообщение"
                aria-label="Голосовое сообщение"
                disabled={sending || uid == null}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="23" />
                  <line x1="8" y1="23" x2="16" y2="23" />
                </svg>
              </button>
              {!isDesktop && !isMaxMessenger ? (
                <button
                  type="button"
                  className="composerActionBtn"
                  onClick={toggleVideoNote}
                  title="Видеокружок"
                  aria-label="Записать видеокружок"
                  disabled={sending || uid == null}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" />
                    <circle cx="12" cy="12" r="4" fill="currentColor" stroke="none" />
                  </svg>
                </button>
              ) : null}
            </span>
          ) : null}
          {(!recording && !vnRecording && (text.trim() || editingMsg)) ? (
            <button className="sendBtn" type="submit" disabled={sending} aria-label={editingMsg ? "Сохранить" : "Отправить"}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          ) : null}
        </form>
      </footer>
      )}

      {imagePreviewIndex != null && chatPhotos[imagePreviewIndex] ? (
        <ChatImageLightbox
          items={chatPhotos}
          index={imagePreviewIndex}
          onClose={closeImagePreview}
          onStep={stepImagePreview}
        />
      ) : null}

      {videoPreviewUrl ? (
        <div
          className="msgVideoLightbox"
          role="dialog"
          aria-label="Просмотр видео"
          onClick={closeVideoPreview}
        >
          <div className="msgVideoLightboxToolbar">
            <button
              type="button"
              className="msgVideoLightboxIconBtn"
              title="Полный экран"
              aria-label="Полный экран"
              onClick={(e) => {
                e.stopPropagation();
                const v = videoLightboxVideoRef.current;
                if (!v) return;
                if (document.fullscreenElement === v) {
                  document.exitFullscreen().catch(() => {});
                } else {
                  v.requestFullscreen?.().catch(() => {});
                }
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
              </svg>
            </button>
            <button
              type="button"
              className="msgVideoLightboxIconBtn"
              title="Скачать"
              aria-label="Скачать видео"
              onClick={(e) => {
                e.stopPropagation();
                downloadMediaFromUrl(videoPreviewUrl);
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </button>
            <button
              type="button"
              className="msgVideoLightboxIconBtn"
              aria-label="Закрыть"
              onClick={(e) => {
                e.stopPropagation();
                closeVideoPreview();
              }}
            >
              ×
            </button>
          </div>
          <video
            ref={videoLightboxVideoRef}
            className="msgVideoLightboxVideo"
            src={videoPreviewUrl}
            controls
            playsInline
            autoPlay
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ) : null}

      <BottomSheetModal
        open={refusalOpen}
        onClose={() => {
          setRefusalOpen(false);
          setRefusalReasonId(null);
        }}
        title="Причина отказа"
      >
        <div className="refusalModalBody">
          {refusalReasons.length === 0 ? (
            <div className="refusalModalEmpty">Нет причин. Добавьте их в настройках.</div>
          ) : (
            <div className="refusalReasonsList">
              {refusalReasons.map((r) => (
                <label key={r.id} className="refusalReasonItem">
                  <input
                    type="radio"
                    name="chatRefusalReason"
                    checked={refusalReasonId === r.id}
                    onChange={() => setRefusalReasonId(r.id)}
                  />
                  <span>{r.title}</span>
                </label>
              ))}
            </div>
          )}
          <div className="refusalModalActions">
            <button
              type="button"
              className="linkBtn dealTaskSubmitBtn"
              disabled={refusalBusy || refusalReasonId == null}
              onClick={handleChatRefuse}
            >
              {refusalBusy ? "…" : "Оформить отказ"}
            </button>
          </div>
        </div>
      </BottomSheetModal>

      <BottomSheetModal
        open={taskCreateOpen}
        onClose={() => {
          if (taskCreateBusy) return;
          setTaskCreateOpen(false);
          setTaskCreateTitle("");
          setTaskCreateDescription("");
          setTaskCreateDueAt("");
        }}
        title="Создать задачу"
      >
        <form
          className="dealTaskForm dealTaskFormInModal"
          onSubmit={handleCreateTaskFromChat}
        >
          <input
            type="text"
            className="input"
            placeholder="Название задачи"
            value={taskCreateTitle}
            onChange={(e) => setTaskCreateTitle(e.target.value)}
            autoFocus
            disabled={taskCreateBusy}
          />
          <textarea
            className="input dealTaskDescription"
            placeholder="Описание (необязательно)"
            value={taskCreateDescription}
            onChange={(e) => setTaskCreateDescription(e.target.value)}
            rows={3}
            disabled={taskCreateBusy}
          />
          <input
            type="datetime-local"
            className="input"
            value={taskCreateDueAt}
            onChange={(e) => setTaskCreateDueAt(e.target.value)}
            title="Срок (необязательно — можно установить позже)"
            disabled={taskCreateBusy}
          />
          <div className="dealTaskFormActions">
            <button
              type="submit"
              className="linkBtn dealTaskSubmitBtn"
              disabled={taskCreateBusy || !taskCreateTitle.trim()}
            >
              {taskCreateBusy ? "…" : "Создать"}
            </button>
          </div>
        </form>
      </BottomSheetModal>

      <BottomSheetModal
        open={clientUnreachableOpen}
        onClose={() => setClientUnreachableOpen(false)}
        title="Сообщение не доставлено"
      >
        <p className="chatErrorText" style={{ marginTop: 0 }}>
          Не удалось отправить сообщение: клиент недоступен в Telegram. Частая причина — пользователь
          заблокировал бота или удалил чат.
        </p>
        <button
          type="button"
          className="linkBtn"
          style={{ marginTop: "12px" }}
          onClick={() => setClientUnreachableOpen(false)}
        >
          Закрыть
        </button>
      </BottomSheetModal>

      <ClientCardModal
        open={cardOpen}
        onClose={() => setCardOpen(false)}
        userId={uid}
        useTelegramId={false}
      />

      <PickClientModal
        open={forwardPickOpen}
        onClose={() => {
          setForwardPickOpen(false);
          setForwardMessageId(null);
        }}
        onPick={handleForwardPick}
        title="Переслать в чат"
        excludeUserId={uid}
        dialogsListMode
      />
    </>
  );
}

const REPLY_THUMB_CONTENT_TYPES = new Set([
  "photo",
  "video",
  "animation",
  "video_note",
  "sticker",
]);

const REPLY_GENERIC_LABELS = new Set([
  "Фото",
  "Видео",
  "Файл",
  "Аудио",
  "Голосовое",
  "GIF",
  "Стикер",
  "Видеосообщение",
  "Контакт",
  "Локация",
  "Опрос",
  "Сообщение",
]);

function resolveReplyThumbUrl(replyTo, replyToId, messages) {
  const fromApi = (replyTo && String(replyTo.thumb_url || "").trim()) || "";
  if (fromApi) return fromApi;
  const pid = Number(replyTo?.id ?? replyToId);
  if (!pid || !messages?.length) return "";
  const parent = messages.find((m) => Number(m.id) === pid);
  const pct = (parent?.content_type || "").toLowerCase();
  if (parent?.file_url && REPLY_THUMB_CONTENT_TYPES.has(pct)) {
    return String(parent.file_url).trim();
  }
  return "";
}

function MessageReplyQuote({ replyTo, replyToId, onGoToReply, messages }) {
  const preview = (replyTo && String(replyTo.preview || "").trim()) || "";
  const targetId = Number(replyTo?.id ?? replyToId);
  const thumbUrl = resolveReplyThumbUrl(replyTo, replyToId, messages);
  const parentMsg =
    targetId > 0 && messages?.length
      ? messages.find((m) => Number(m.id) === targetId)
      : null;
  const ct = (replyTo?.content_type || parentMsg?.content_type || "").toLowerCase();
  const showThumb = !!thumbUrl;
  const hideGenericLabel = showThumb && REPLY_GENERIC_LABELS.has(preview);
  if (!preview && !showThumb) return null;
  const canJump = targetId > 0 && typeof onGoToReply === "function";
  const thumbRound = ct === "video_note";
  return (
    <button
      type="button"
      className={`msgReplyQuote${canJump ? " msgReplyQuote--clickable" : ""}${showThumb ? " msgReplyQuote--withThumb" : ""}`}
      title={canJump ? "Перейти к сообщению" : "Ответ на сообщение"}
      disabled={!canJump}
      onClick={(e) => {
        e.stopPropagation();
        if (canJump) onGoToReply(targetId);
      }}
    >
      <span className="msgReplyQuoteBar" aria-hidden="true" />
      <span className="msgReplyQuoteBody">
        {!hideGenericLabel && preview ? (
          <span className="msgReplyQuoteText">
            <MsgTextWithLinks text={preview} />
          </span>
        ) : null}
        {showThumb ? (
          ct === "video" || ct === "animation" ? (
            <video
              className={`msgReplyQuoteThumb${thumbRound ? " msgReplyQuoteThumb--round" : ""}`}
              src={thumbUrl}
              muted
              playsInline
              preload="metadata"
              aria-hidden="true"
            />
          ) : (
            <img
              className={`msgReplyQuoteThumb${thumbRound ? " msgReplyQuoteThumb--round" : ""}`}
              src={thumbUrl}
              alt=""
              loading="lazy"
              decoding="async"
            />
          )
        ) : null}
      </span>
    </button>
  );
}

function DocumentIcon() {
  return (
    <svg className="msgDocumentIconSvg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  );
}

/** Все фото чата по порядку (для просмотра с листанием, как в Telegram). */
function collectChatPhotos(messages) {
  if (!messages?.length) return [];
  const sorted = [...messages].sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0));
  const out = [];
  for (const m of sorted) {
    if ((m.content_type || "").toLowerCase() !== "photo") continue;
    if (!m.file_url && m.file_id == null) continue;
    out.push({
      messageId: m.id,
      url: m.file_url || null,
      fileId: m.file_id != null ? String(m.file_id) : null,
    });
  }
  return out;
}

const IMAGE_LIGHTBOX_SWIPE_PX = 48;

function ChatImageLightbox({ items, index, onClose, onStep }) {
  const item = items[index];
  const swipeRef = useRef({ x: 0, y: 0, active: false });
  const canPrev = index > 0;
  const canNext = index < items.length - 1;

  const onTouchStart = useCallback((e) => {
    const t = e.touches?.[0];
    if (!t) return;
    swipeRef.current = { x: t.clientX, y: t.clientY, active: true };
  }, []);

  const onTouchEnd = useCallback(
    (e) => {
      if (!swipeRef.current.active) return;
      const t = e.changedTouches?.[0];
      swipeRef.current.active = false;
      if (!t) return;
      const dx = t.clientX - swipeRef.current.x;
      const dy = t.clientY - swipeRef.current.y;
      if (Math.abs(dx) < IMAGE_LIGHTBOX_SWIPE_PX || Math.abs(dx) < Math.abs(dy)) return;
      if (dx < 0 && canNext) onStep(1);
      else if (dx > 0 && canPrev) onStep(-1);
    },
    [canNext, canPrev, onStep]
  );

  if (!item) return null;

  return (
    <div
      className="msgImageLightbox"
      role="dialog"
      aria-label="Просмотр изображения"
      aria-modal="true"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="msgImageLightboxToolbar" onClick={(e) => e.stopPropagation()}>
        {items.length > 1 ? (
          <span className="msgImageLightboxCounter" aria-live="polite">
            {index + 1} / {items.length}
          </span>
        ) : null}
        <button
          type="button"
          className="msgImageLightboxIconBtn"
          aria-label="Скачать изображение"
          title="Скачать"
          onClick={(e) => {
            e.stopPropagation();
            const url = item.url;
            if (url) downloadMediaFromUrl(url);
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
        </button>
        <button type="button" className="msgImageLightboxIconBtn" aria-label="Закрыть" onClick={onClose}>
          ×
        </button>
      </div>

      {canPrev ? (
        <button
          type="button"
          className="msgImageLightboxNav msgImageLightboxNav--prev"
          aria-label="Предыдущее фото"
          onClick={(e) => {
            e.stopPropagation();
            onStep(-1);
          }}
        >
          ‹
        </button>
      ) : null}
      {canNext ? (
        <button
          type="button"
          className="msgImageLightboxNav msgImageLightboxNav--next"
          aria-label="Следующее фото"
          onClick={(e) => {
            e.stopPropagation();
            onStep(1);
          }}
        >
          ›
        </button>
      ) : null}

      <div className="msgImageLightboxStage" onClick={(e) => e.stopPropagation()}>
        <ChatLightboxImage key={item.messageId} url={item.url} fileId={item.fileId} />
      </div>
    </div>
  );
}

function ChatLightboxImage({ url, fileId }) {
  const [displaySrc, setDisplaySrc] = useState(() => {
    if (!url) return "";
    if (fileId) return getCachedChatMediaUrl(String(fileId)) || "";
    return url;
  });

  useEffect(() => {
    let cancelled = false;
    if (!url) {
      setDisplaySrc("");
      return () => {
        cancelled = true;
      };
    }
    const fid = fileId ? String(fileId) : "";
    const cached = fid ? getCachedChatMediaUrl(fid) : null;
    if (cached) {
      setDisplaySrc(cached);
      return () => {
        cancelled = true;
      };
    }
    if (fid) {
      loadChatMediaIntoCache(fid, url)
        .then((blobUrl) => {
          if (!cancelled) setDisplaySrc(blobUrl);
        })
        .catch(() => {
          if (!cancelled) setDisplaySrc(url);
        });
      return () => {
        cancelled = true;
      };
    }
    setDisplaySrc(url);
    return () => {
      cancelled = true;
    };
  }, [url, fileId]);

  const src = displaySrc || url;
  if (!src) return null;

  return <img className="msgImageLightboxImg" src={src} alt="Просмотр изображения" draggable={false} />;
}

/** Ключ группы фото (как альбом в Telegram): media_group_id + направление. */
function _photoAlbumKey(m) {
  const mg = m?.media_group_id;
  const ct = (m?.content_type || "").toLowerCase();
  if (!mg || ct !== "photo") return null;
  return `${mg}|${String(m?.direction ?? "")}`;
}

/**
 * Группирует сообщения для отрисовки: все фото одного media_group_id — в один пузырь
 * (даже если из-за пагинации или порядка id в массиве они не подряд).
 */
function messageGroups(messages) {
  if (!messages?.length) return [];
  const sorted = [...messages].sort(compareMessagesAsc);
  const assigned = new Set();
  const rawGroups = [];

  for (const m of sorted) {
    if (assigned.has(m.id)) continue;
    const k = _photoAlbumKey(m);
    if (!k) {
      rawGroups.push([m]);
      assigned.add(m.id);
      continue;
    }
    const album = sorted.filter((x) => _photoAlbumKey(x) === k);
    album.forEach((x) => assigned.add(x.id));
    album.sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0));
    rawGroups.push(album);
  }

  return rawGroups;
}

function MessageAlbum({ group, uid, onOpenImage, onOpenMenu, onGoToReply, messages, myTelegramId, displayTz }) {
  const isIn = isClientMessage(group[0]);
  const captionMsg = group.find((m) => (m.caption || m.text || "").trim());
  const body = captionMsg ? (captionMsg.caption || captionMsg.text || "").trim() : "";
  const time = fmtTimeInZone(group[0].created_at, displayTz);
  const hasCaption = !!body;
  const first = group[0];
  const menuHandler = useCallback(
    (e) => onOpenMenu?.(first, e.clientX, e.clientY),
    [first, onOpenMenu]
  );
  const { handlers: lpHandlers, wrapParentClickCapture } = useLongPressContextMenu(
    onOpenMenu ? menuHandler : null
  );

  const albumMessageIds = group.map((m) => m.id).filter((id) => id != null).join(",");

  return (
    <div
      className={`msg ${isIn ? "in" : "out"} msgHasPhoto msgAlbumBubble ${hasCaption ? "msgHasCaption" : "msgNoCaption"}`}
      data-message-ids={albumMessageIds || undefined}
      onClickCapture={wrapParentClickCapture}
      {...lpHandlers}
    >
      <div className="content">
        <MessageReplyQuote
          replyTo={group[0]?.reply_to}
          replyToId={group[0]?.reply_to_id}
          onGoToReply={onGoToReply}
          messages={messages}
        />
        <div className="msgMedia msgAlbum">
          <div className="msgMediaFrame">
            <div className="msgAlbumGrid">
              {group.map((m) => (
                <MessagePhoto key={m.id} m={m} uid={uid} onOpenImage={onOpenImage} onOpenMenu={onOpenMenu} />
              ))}
            </div>
            {!hasCaption ? <span className="msgTime msgTimeOnMedia">{time}</span> : null}
          </div>
          {hasCaption ? (
            <div className="msgCaption msgCaptionWithTime">
              <MsgTextWithLinks text={body} />
              <span className="msgTime msgTimeInCaption">{time}</span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function PhotoButton({ url, imgClassName, onOpenImage, fileId, messageId, message, onOpenMenu }) {
  const [status, setStatus] = useState("loading"); // loading | decoding | loaded | error
  const [displaySrc, setDisplaySrc] = useState(() => {
    if (!url) return "";
    if (fileId) return getCachedChatMediaUrl(String(fileId)) || "";
    return url;
  });
  const imgRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    if (!url) {
      setDisplaySrc("");
      return () => {
        cancelled = true;
      };
    }
    const fid = fileId ? String(fileId) : "";
    const cached = fid ? getCachedChatMediaUrl(fid) : null;
    if (cached) {
      setDisplaySrc(cached);
      return () => {
        cancelled = true;
      };
    }
    if (fid) {
      loadChatMediaIntoCache(fid, url)
        .then((blobUrl) => {
          if (!cancelled) setDisplaySrc(blobUrl);
        })
        .catch(() => {
          if (!cancelled) setDisplaySrc(url);
        });
      return () => {
        cancelled = true;
      };
    }
    setDisplaySrc(url);
    return () => {
      cancelled = true;
    };
  }, [url, fileId]);

  useEffect(() => {
    setStatus("loading");
  }, [displaySrc, url]);

  const isLoading = status === "loading" || status === "decoding";
  const isError = status === "error";
  const resolving = Boolean(fileId && url && !displaySrc);
  const openSrc = displaySrc || url;

  const handlePhotoContextMenu = useCallback(
    (e) => {
      if (!onOpenMenu || !message) return;
      e.preventDefault();
      e.stopPropagation();
      onOpenMenu(message, e.clientX, e.clientY);
    },
    [message, onOpenMenu]
  );

  return (
    <button
      type="button"
      onClick={() => {
        if (!openSrc || messageId == null) return;
        onOpenImage?.({ messageId, url: openSrc });
      }}
      onContextMenu={onOpenMenu && message ? handlePhotoContextMenu : undefined}
      className={`msgPhotoBtn${isLoading || resolving ? " msgPhotoBtn--busy" : ""}`}
      aria-label="Открыть изображение"
      disabled={!openSrc}
    >
      {displaySrc ? (
        <img
          ref={imgRef}
          src={displaySrc}
          alt=""
          className={imgClassName}
          loading="eager"
          decoding="async"
          onLoad={async () => {
            // В Telegram WebView бывает лаг между onLoad и реальной отрисовкой (декодированием).
            // Держим лоадер до decode()/следующего кадра.
            setStatus("decoding");
            const el = imgRef.current;
            try {
              if (el && typeof el.decode === "function") {
                await el.decode();
              }
            } catch {
              // ignore
            }
            requestAnimationFrame(() => {
              requestAnimationFrame(() => setStatus("loaded"));
            });
          }}
          onError={() => setStatus("error")}
          style={status === "loaded" ? undefined : { opacity: 0.6 }}
        />
      ) : !url ? (
        <span aria-hidden="true" style={{ display: "block", width: "100%", height: "100%" }} />
      ) : null}
      {isLoading || resolving ? (
        <span className="msgPhotoOverlay" aria-label="Загрузка изображения">
          <span className="msgPhotoSpinner" aria-hidden="true" />
        </span>
      ) : null}
      {isError ? <span className="msgPhotoOverlay">Не удалось загрузить</span> : null}
    </button>
  );
}

function MessagePhoto({ m, uid, onOpenImage, onOpenMenu }) {
  return (
    <PhotoButton
      url={m.file_url || null}
      imgClassName="msgPhoto"
      onOpenImage={onOpenImage}
      fileId={m.file_id != null ? String(m.file_id) : undefined}
      messageId={m.id}
      message={m}
      onOpenMenu={onOpenMenu}
    />
  );
}

function formatTime(seconds) {
  if (Number.isFinite(seconds) && seconds >= 0) {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  }
  return "0:00";
}

function VoiceMessagePlayer({ messageId, src, isOut, ariaLabel }) {
  const key = String(messageId || src || "");
  const seekingRef = useRef(false);
  const [playbackTick, setPlaybackTick] = useState(0);

  useEffect(() => subscribeVoicePlayback(() => setPlaybackTick((t) => t + 1)), []);

  useEffect(() => {
    if (key && src) prefetchVoice(key, src);
  }, [key, src]);

  const snap = getVoicePlaybackSnapshot(key);
  const playing = snap.playing;
  const currentTime = snap.isActive ? snap.currentTime : 0;
  const duration = snap.duration || 0;
  void playbackTick;
  const loadStatus =
    snap.isActive && snap.loadStatus === "loading" ? "loading" : snap.loadStatus === "ready" ? "ready" : "idle";
  const speed = snap.speed;

  const togglePlay = useCallback(() => {
    if (!key || !src) return;
    toggleVoicePlayback(key, src);
  }, [key, src]);

  const onSeek = useCallback(
    (e) => {
      const v = parseFloat(e.target.value, 10);
      if (Number.isFinite(v)) seekVoicePlayback(key, v);
    },
    [key]
  );

  const toggleSpeed = useCallback(() => {
    cycleVoiceSpeed();
  }, []);

  return (
    <div className={`msgVoiceWrap ${isOut ? "msgVoiceWrapOut" : ""}`} role="region" aria-label={ariaLabel}>
      <button
        type="button"
        className={`msgVoicePlayBtn ${loadStatus === "loading" ? "isLoading" : ""}`}
        onClick={togglePlay}
        aria-label={loadStatus === "loading" ? "Загрузка голосового" : playing ? "Пауза" : "Воспроизвести"}
        disabled={loadStatus === "loading"}
      >
        {loadStatus === "loading" ? (
          <span className="msgVoiceSpinner" aria-hidden="true" />
        ) : playing ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <rect x="6" y="4" width="4" height="16" rx="1" />
            <rect x="14" y="4" width="4" height="16" rx="1" />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
      </button>
      <button
        type="button"
        className="msgVoiceSpeedBtn"
        onClick={toggleSpeed}
        aria-label={`Скорость воспроизведения ${speed}x`}
        title={`Скорость: ${speed}x`}
      >
        {speed}x
      </button>
      <div className="msgVoiceProgressBlock">
        <input
          type="range"
          className="msgVoiceProgress"
          min={0}
          max={duration > 0 ? duration : 1}
          step={0.1}
          value={Math.min(currentTime, duration > 0 ? duration : 1)}
          onPointerDown={() => {
            seekingRef.current = true;
          }}
          onPointerUp={() => {
            seekingRef.current = false;
          }}
          onTouchStart={() => {
            seekingRef.current = true;
          }}
          onTouchEnd={() => {
            seekingRef.current = false;
          }}
          onInput={onSeek}
          aria-label="Прогресс воспроизведения"
          disabled={!snap.isActive || loadStatus !== "ready" || duration <= 0}
        />
        <div className="msgVoiceTime">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ m, uid, onOpenImage, onOpenVideo, onOpenMenu, onGoToReply, messages, myTelegramId, displayTz }) {
  const isIn = isClientMessage(m);
  const body = (m.text || m.caption || "").trim();
  const [fileUrl, setFileUrl] = useState(m.file_url || null);
  const hasMedia = !!(m.file_url || fileUrl || m.file_id);
  const mediaUrl = fileUrl || m.file_url;
  const fileName = (m.file_name || "").trim();
  const ct = (m.content_type || "").toLowerCase();
  const isPendingVideoNote = ct === "video_note" && !!m._localPending;
  const isVideoNote = ct === "video_note" && hasMedia;
  const isVideoType = ct === "video" || ct === "animation";
  const isVideoDocument =
    ct === "document" &&
    hasMedia &&
    /\.(mp4|webm|mov|m4v|avi|mkv)$/i.test(fileName);
  const usePhotoBubbleWidth =
    (ct === "photo" && hasMedia) ||
    (isVideoType && hasMedia) ||
    (hasMedia && !["voice", "audio", "document", "video_note"].includes(ct) && !isVideoType);
  const time = fmtTimeInZone(m.created_at, displayTz);
  const hasCaption = !!body;
  const isOwn = isManagerMessage(m) && myTelegramId != null && Number(m.manager_telegram_id) === Number(myTelegramId);

  const menuHandler = useCallback(
    (e) => onOpenMenu?.(m, e.clientX, e.clientY),
    [m, onOpenMenu]
  );
  const { handlers: lpHandlers, wrapParentClickCapture } = useLongPressContextMenu(
    onOpenMenu ? menuHandler : null
  );

  useEffect(() => {
    // /file-url больше не используем: backend всегда отдаёт file_url в сообщениях/WS-payload.
  }, []);

  return (
    <div
      className={`msg ${isIn ? "in" : "out"} ${isVideoNote || isPendingVideoNote ? "msgHasVideoNote" : ""} ${usePhotoBubbleWidth ? "msgHasPhoto" : ""} ${hasCaption ? "msgHasCaption" : "msgNoCaption"}`}
      data-message-id={m.id != null ? String(m.id) : undefined}
      onClickCapture={wrapParentClickCapture}
      {...lpHandlers}
    >
      <div className="content">
        <MessageReplyQuote
          replyTo={m.reply_to}
          replyToId={m.reply_to_id}
          onGoToReply={onGoToReply}
          messages={messages}
        />
        {(ct === "photo" && (m.file_id || m.file_url)) ? (
          <div className="msgMedia msgMediaPhoto">
            <div className="msgMediaFrame">
              <PhotoButton
                url={m.file_url || fileUrl || null}
                imgClassName="msgPhoto msgPhotoFull"
                onOpenImage={onOpenImage}
                fileId={m.file_id != null ? String(m.file_id) : undefined}
                messageId={m.id}
                message={m}
                onOpenMenu={onOpenMenu}
              />
              {!hasCaption ? <span className="msgTime msgTimeOnMedia">{time}</span> : null}
            </div>
            {hasCaption ? (
              <div className="msgCaption msgCaptionWithTime">
                <MsgTextWithLinks text={body} />
                <span className="msgTime msgTimeInCaption">{time}</span>
              </div>
            ) : null}
          </div>
        ) : (ct === "voice" || ct === "audio") && hasMedia ? (
          <div className="msgMedia">
            <VoiceMessagePlayer
              messageId={m.id}
              src={mediaUrl}
              isOut={!isIn}
              ariaLabel={ct === "voice" ? "Голосовое сообщение" : "Аудио"}
            />
            {body ? (
              <div className="msgCaption">
                <MsgTextWithLinks text={body} />
              </div>
            ) : null}
          </div>
        ) : isPendingVideoNote ? (
          <div className="msgMedia msgVideoNoteWrap">
            <div className="msgMediaFrame msgVideoNoteFrame">
              <div className="msgVideoNoteBtn msgVideoNoteBtnPending" aria-label="Видеокружок отправляется">
                <span className="msgVideoNotePendingLoader" aria-hidden="true" />
              </div>
              {!hasCaption ? <span className="msgTime msgTimeOnMedia msgTimeOnVideoNote">{time}</span> : null}
            </div>
            {hasCaption ? (
              <div className="msgCaption msgCaptionWithTime">
                <MsgTextWithLinks text={body} />
                <span className="msgTime msgTimeInCaption">{time}</span>
              </div>
            ) : null}
          </div>
        ) : isVideoNote ? (
          <div className="msgMedia msgVideoNoteWrap">
            <div className="msgMediaFrame msgVideoNoteFrame">
              <button
                type="button"
                className="msgVideoNoteBtn"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenVideo?.(mediaUrl);
                }}
                aria-label="Открыть видеокружок"
              >
                <video
                  className="msgVideoNotePreview"
                  src={mediaUrl}
                  muted
                  loop
                  playsInline
                  autoPlay
                  preload="metadata"
                />
                <span className="msgVideoNotePlayHint" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </span>
              </button>
              {!hasCaption ? <span className="msgTime msgTimeOnMedia msgTimeOnVideoNote">{time}</span> : null}
            </div>
            {hasCaption ? (
              <div className="msgCaption msgCaptionWithTime">
                <MsgTextWithLinks text={body} />
                <span className="msgTime msgTimeInCaption">{time}</span>
              </div>
            ) : null}
          </div>
        ) : isVideoType && hasMedia ? (
          <div className="msgMedia msgVideoBlock">
            <button
              type="button"
              className="msgVideoPreviewBtn"
              onClick={(e) => {
                e.stopPropagation();
                onOpenVideo?.(mediaUrl);
              }}
              aria-label="Открыть видео на весь экран"
            >
              <div className="msgVideoInlineWrap">
              <video
                className="msgVideoInline"
                src={mediaUrl}
                autoPlay
                loop
                playsInline
                preload="metadata"
                muted
                onClick={(e) => e.preventDefault()}
              />
                <span className="msgVideoPlayHint" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </span>
              </div>
            </button>
            {body ? (
              <div className="msgCaption">
                <MsgTextWithLinks text={body} />
              </div>
            ) : null}
          </div>
        ) : isVideoDocument ? (
          <div className="msgMedia">
            <button
              type="button"
              className="msgDocVideoMini"
              onClick={(e) => {
                e.stopPropagation();
                onOpenVideo?.(mediaUrl);
              }}
              aria-label="Открыть видео"
            >
              <span className="msgDocVideoMiniIcon" aria-hidden="true">
                <DocumentIcon />
              </span>
              <span className="msgDocVideoMiniText">
                <span className="msgDocVideoMiniTitle">{fileName || "Видеофайл"}</span>
                <span className="msgDocVideoMiniMeta">Видео файл</span>
              </span>
            </button>
            {body ? (
              <div className="msgCaption">
                <MsgTextWithLinks text={body} />
              </div>
            ) : null}
          </div>
        ) : ct === "document" && hasMedia ? (
          <div className="msgMedia">
            <a href={mediaUrl} download={m.file_name ?? "file"} target="_blank" rel="noopener noreferrer" className="msgDocumentLink">
              <span className="msgDocumentIcon" aria-hidden="true">
                <DocumentIcon />
              </span>
              <span className="msgDocumentName">{m.file_name || "Документ"}</span>
            </a>
            {body ? (
              <div className="msgCaption">
                <MsgTextWithLinks text={body} />
              </div>
            ) : null}
          </div>
        ) : hasMedia ? (
          <div className="msgMedia">
            <div className="msgMediaFrame">
              <PhotoButton
                url={mediaUrl}
                imgClassName="msgPhoto msgPhotoFull"
                onOpenImage={onOpenImage}
                fileId={m.file_id != null ? String(m.file_id) : undefined}
                messageId={m.id}
              />
              {!hasCaption ? <span className="msgTime msgTimeOnMedia">{time}</span> : null}
            </div>
            {hasCaption ? (
              <div className="msgCaption msgCaptionWithTime">
                <MsgTextWithLinks text={body} />
                <span className="msgTime msgTimeInCaption">{time}</span>
              </div>
            ) : null}
          </div>
        ) : body ? (
          <>
            <div className="msgText">
              <MsgTextWithLinks text={body} />
            </div>
            <span className="msgTime msgTimeInBubble">{time}</span>
          </>
        ) : (
          <span className="pill">{m.content_type || "—"}</span>
        )}
      </div>
      {(hasMedia && ct !== "photo" && ct !== "video_note") ? <span className="msgTime msgTimeInBubble">{time}</span> : null}
    </div>
  );
}

